/**
 * Badge Component - AWLP Design System
 * Display status indicators and role badges with color variants
 */

import { ReactNode } from 'react';
import styled, { css } from 'styled-components';

export type BadgeVariant = 'primary' | 'success' | 'warning' | 'error' | 'info' | 'neutral';

export interface BadgeProps {
  variant?: BadgeVariant;
  roleName?: string;
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

const getRoleStyles = (roleName: string) => {
  const normalizedRole = roleName.toLowerCase().replace(/\s+/g, '');

  if (normalizedRole.includes('orgadmin') || normalizedRole.includes('organizationadmin')) {
    return css`
      background: ${({ theme }) => theme.colors.roles.orgAdmin.bg};
      color: ${({ theme }) => theme.colors.roles.orgAdmin.text};
      border: 1px solid ${({ theme }) => theme.colors.roles.orgAdmin.text}33;
    `;
  }

  if (normalizedRole.includes('supervisor') || normalizedRole.includes('bcba')) {
    return css`
      background: ${({ theme }) => theme.colors.roles.supervisor.bg};
      color: ${({ theme }) => theme.colors.roles.supervisor.text};
      border: 1px solid ${({ theme }) => theme.colors.roles.supervisor.text}33;
    `;
  }

  if (normalizedRole.includes('technician') || normalizedRole.includes('rbt')) {
    return css`
      background: ${({ theme }) => theme.colors.roles.technician.bg};
      color: ${({ theme }) => theme.colors.roles.technician.text};
      border: 1px solid ${({ theme }) => theme.colors.roles.technician.text}33;
    `;
  }

  // Default role style
  return css`
    background: ${({ theme }) => theme.colors.backgrounds.light};
    color: ${({ theme }) => theme.colors.text.primary};
    border: 1px solid ${({ theme }) => theme.colors.borders.light};
  `;
};

const getVariantStyles = (variant: BadgeVariant) => {
  const variants = {
    primary: css`
      background: ${({ theme }) => theme.colors.status.info.bg};
      color: ${({ theme }) => theme.colors.status.info.text};
      border: 1px solid ${({ theme }) => theme.colors.status.info.border};
    `,

    success: css`
      background: ${({ theme }) => theme.colors.status.success.bg};
      color: ${({ theme }) => theme.colors.status.success.text};
      border: 1px solid ${({ theme }) => theme.colors.status.success.border};
    `,

    warning: css`
      background: ${({ theme }) => theme.colors.status.warning.bg};
      color: ${({ theme }) => theme.colors.status.warning.text};
      border: 1px solid ${({ theme }) => theme.colors.status.warning.border};
    `,

    error: css`
      background: ${({ theme }) => theme.colors.status.error.bg};
      color: ${({ theme }) => theme.colors.status.error.text};
      border: 1px solid ${({ theme }) => theme.colors.status.error.border};
    `,

    info: css`
      background: ${({ theme }) => theme.colors.status.info.bg};
      color: ${({ theme }) => theme.colors.status.info.text};
      border: 1px solid ${({ theme }) => theme.colors.status.info.border};
    `,

    neutral: css`
      background: ${({ theme }) => theme.colors.backgrounds.light};
      color: ${({ theme }) => theme.colors.text.secondary};
      border: 1px solid ${({ theme }) => theme.colors.borders.light};
    `,
  };

  return variants[variant];
};

const StyledBadge = styled.span<{ $variant?: BadgeVariant; $roleName?: string }>`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  white-space: nowrap;

  ${({ $roleName }) => $roleName && getRoleStyles($roleName)}
  ${({ $variant, $roleName }) => !$roleName && $variant && getVariantStyles($variant)}
`;

export const Badge = ({ variant = 'neutral', roleName, children, className, style }: BadgeProps) => {
  return (
    <StyledBadge $variant={variant} $roleName={roleName} className={className} style={style}>
      {children}
    </StyledBadge>
  );
};

export default Badge;
